function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
const {
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext
} = React;
function Reveal({
  children,
  cls = '',
  delay = 0,
  as = 'div',
  style
}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const show = () => {
      el.classList.add('in');
    };
    const io = new IntersectionObserver(es => {
      es.forEach(e => {
        if (e.isIntersecting) {
          show();
          io.unobserve(el);
        }
      });
    }, {
      threshold: .12,
      rootMargin: '0px 0px -8% 0px'
    });
    io.observe(el);
    const t = setTimeout(() => {
      if (el.classList.contains('in')) return;
      const r = el.getBoundingClientRect();
      if (r.top < (window.innerHeight || 0) * 0.96) {
        show();
        io.unobserve(el);
      }
    }, 1200);
    return () => {
      io.disconnect();
      clearTimeout(t);
    };
  }, []);
  const Tag = as;
  return React.createElement(Tag, {
    ref: ref,
    className: 'rv ' + cls,
    style: {
      transitionDelay: delay ? delay + 's' : undefined,
      ...style
    }
  }, children);
}
function Marquee({
  items,
  sep = '\u2009\u2022\u2009',
  speed = 40
}) {
  const row = items.join(sep) + sep;
  const track = useRef(null);
  useEffect(() => {
    const el = track.current;
    if (!window.gsap || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.style.animation = 'none';
    const tween = gsap.to(el, {
      xPercent: -33.333,
      ease: 'none',
      repeat: -1,
      duration: speed
    });
    const st = ScrollTrigger.create({
      onUpdate(self) {
        const v = self.getVelocity();
        if (Math.abs(v) < 60) return;
        const dir = v < 0 ? -1 : 1;
        const boost = gsap.utils.clamp(1, 4.5, Math.abs(v) / 500);
        gsap.to(tween, {
          timeScale: dir * boost,
          duration: 0.2,
          overwrite: true,
          onComplete: () => gsap.to(tween, {
            timeScale: dir,
            duration: 1,
            overwrite: true
          })
        });
        gsap.to(el, {
          skewX: gsap.utils.clamp(-5, 5, -v / 400),
          duration: 0.25,
          overwrite: 'auto',
          onComplete: () => gsap.to(el, {
            skewX: 0,
            duration: 0.6
          })
        });
      }
    });
    return () => {
      tween.kill();
      st.kill();
    };
  }, []);
  return React.createElement("div", {
    className: "marquee"
  }, React.createElement("div", {
    className: "marquee-track",
    ref: track,
    style: {
      animationDuration: speed + 's'
    }
  }, React.createElement("span", null, row), React.createElement("span", null, row), React.createElement("span", null, row)));
}
function Turntable({
  frames,
  alt = '',
  className = '',
  auto = 0
}) {
  const wrap = useRef(null),
    a = useRef(null),
    b = useRef(null);
  const st = useRef({
    i: 0,
    top: 'a',
    hov: false
  });
  useEffect(() => {
    frames.forEach(f => {
      const i = new Image();
      i.src = f;
    });
  }, []);
  const show = (i, fade) => {
    const s = st.current,
      n = frames.length;
    i = (i % n + n) % n;
    if (i === s.i || !a.current || !b.current) return;
    s.i = i;
    const top = s.top === 'a' ? a.current : b.current;
    const bot = s.top === 'a' ? b.current : a.current;
    if (fade && window.gsap) {
      bot.src = frames[i];
      gsap.to(top, {
        opacity: 0,
        duration: .5,
        ease: 'power1.inOut'
      });
      gsap.fromTo(bot, {
        opacity: 0
      }, {
        opacity: 1,
        duration: .5,
        ease: 'power1.inOut'
      });
      s.top = s.top === 'a' ? 'b' : 'a';
    } else {
      top.src = frames[i];
    }
  };
  useEffect(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => {
      show(st.current.i + 1, true);
    }, auto || 3000);
    return () => clearInterval(id);
  }, []);
  return React.createElement("div", {
    className: 'turntable ' + className,
    ref: wrap
  }, React.createElement("img", {
    ref: a,
    src: frames[0],
    alt: alt
  }), React.createElement("img", {
    ref: b,
    src: frames[0],
    alt: "",
    "aria-hidden": "true",
    style: {
      opacity: 0
    }
  }));
}
function CountUp({
  value,
  suffix = '',
  dur = 1400
}) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver(es => {
      if (es[0].isIntersecting && !done.current) {
        done.current = true;
        const t0 = performance.now();
        const tick = t => {
          const p = Math.min(1, (t - t0) / dur);
          const e = 1 - Math.pow(1 - p, 3);
          setN(value * e);
          if (p < 1) requestAnimationFrame(tick);else setN(value);
        };
        requestAnimationFrame(tick);
      }
    }, {
      threshold: .4
    });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [value]);
  const disp = Number.isInteger(value) ? Math.round(n) : n.toFixed(1);
  return React.createElement("span", {
    ref: ref
  }, disp, suffix);
}
const DND_PATH = "M0 0H104A64 88 0 0 1 104 176H0V64H32V144H104A32 56 0 0 0 104 32H0ZM208 0H240L336 124V0H368V176H336L240 52V176H208ZM408 0H512A64 88 0 0 1 512 176H408V112H440V144H512A32 56 0 0 0 512 32H408Z";
function DndMark() {
  return React.createElement("svg", {
    className: "dnd-mark",
    viewBox: "0 0 576 176",
    "aria-hidden": "true",
    focusable: "false"
  }, React.createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    d: DND_PATH
  }));
}
function Wordmark({
  size = 26,
  color,
  drink = false
}) {
  return React.createElement("span", {
    className: "wordmark",
    role: "img",
    "aria-label": (drink ? 'Drink ' : '') + 'DND',
    style: {
      fontSize: size,
      color: color || 'var(--ink)'
    }
  }, drink && React.createElement("em", {
    className: "wm-drink",
    "aria-hidden": "true"
  }, "Drink"), React.createElement(DndMark, null));
}
const Icon = {
  drop: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M12 3.2C12 3.2 5.5 10 5.5 14.6a6.5 6.5 0 0 0 13 0C18.5 10 12 3.2 12 3.2Z",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.3"
  })),
  hex: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M12 2.6 20 7v10l-8 4.4L4 17V7l8-4.4Z",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.3"
  }), React.createElement("path", {
    d: "m8.8 12 2.2 2.2 4.4-4.4",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.3"
  })),
  flask: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M9.5 3v6.2L4.7 17.6A2 2 0 0 0 6.45 20.6h11.1a2 2 0 0 0 1.75-2.98L14.5 9.2V3",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.3"
  }), React.createElement("path", {
    d: "M8.5 3h7M8.3 14h7.4",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.3"
  })),
  leaf: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M20 4S8 3 5 11c-2 5.3 2.5 9 2.5 9M20 4c0 9-6 13-12.5 16M20 4c0 4-1 6-1 6",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.3"
  })),
  recycle: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), [0, 120, 240].map(a => React.createElement("g", {
    key: a,
    transform: 'rotate(' + a + ' 12 12.5)'
  }, React.createElement("path", {
    d: "M10.75 6.66 7.15 12.9",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.6"
  }), React.createElement("path", {
    d: "M8.28 13.55 6.02 12.25 6.15 14.63 Z",
    fill: "currentColor"
  })))),
  bag: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M6 8h12l-1 12H7L6 8Z",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.4"
  }), React.createElement("path", {
    d: "M9 8a3 3 0 0 1 6 0",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.4"
  })),
  arrow: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M5 12h14M13 6l6 6-6 6",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5"
  })),
  plus: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M12 5v14M5 12h14",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.6"
  })),
  minus: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M5 12h14",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.6"
  })),
  close: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M6 6l12 12M18 6 6 18",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5"
  })),
  star: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "M12 3.5 14.6 9l6 .6-4.5 4 1.3 5.9L12 16.6 6.6 19.5 7.9 13.6 3.4 9.6l6-.6L12 3.5Z",
    fill: "currentColor"
  })),
  chev: p => React.createElement("svg", _extends({
    viewBox: "0 0 24 24"
  }, p), React.createElement("path", {
    d: "m8 10 4 4 4-4",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5"
  }))
};
Object.assign(window, {
  Reveal,
  Marquee,
  Turntable,
  CountUp,
  Wordmark,
  DndMark,
  Icon
});
;
function Can3D({
  texture,
  fallback,
  alt = '',
  ctrl = null,
  spin = true
}) {
  const host = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!window.THREE || !window.DND_CAN) {
      setFailed(true);
      return;
    }
    const el = host.current;
    if (!el) return;
    let renderer,
      raf = 0,
      disposed = false;
    try {
      renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true
      });
    } catch (e) {
      setFailed(true);
      return;
    }
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.outputEncoding = THREE.sRGBEncoding;
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const cam = new THREE.PerspectiveCamera(20, 1, 0.1, 100);
    cam.position.set(0, 0.5, 10.8);
    cam.lookAt(0, 0, 0);
    const group = new THREE.Group();
    scene.add(group);
    const Hcan = 3.3,
      Rmax = Hcan * window.DND_CAN.aspect / 2;
    const pts = [...window.DND_CAN.profile].reverse().map(([r, y]) => new THREE.Vector2(Math.max(0.002, r * Rmax), (y - 0.5) * Hcan));
    const geo = new THREE.LatheGeometry(pts, 96);
    const tex = new THREE.TextureLoader().load(texture, () => {
      if (!disposed) renderer.render(scene, cam);
    }, undefined, () => {
      if (!disposed) setFailed(true);
    });
    tex.encoding = THREE.sRGBEncoding;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy ? renderer.capabilities.getMaxAnisotropy() : 8;
    const can = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
      map: tex,
      side: THREE.DoubleSide
    }));
    can.rotation.y = -Math.PI / 4;
    group.add(can);
    const topPt = pts[pts.length - 1];
    const lid = new THREE.Mesh(new THREE.CircleGeometry(topPt.x * 0.985, 64), new THREE.MeshBasicMaterial({
      color: 0xaeb2b8
    }));
    lid.rotation.x = -Math.PI / 2;
    lid.position.y = topPt.y - 0.002;
    group.add(lid);
    const fit = () => {
      const W = el.clientWidth || 300,
        Hp = el.clientHeight || 560;
      renderer.setSize(W, Hp);
      cam.aspect = W / Hp;
      cam.updateProjectionMatrix();
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    let visible = true;
    const io = new IntersectionObserver(es => {
      visible = es[0].isIntersecting;
    });
    io.observe(el);
    const t0 = performance.now();
    const rotTarget = {
      v: -Math.PI / 4
    };
    if (ctrl) ctrl.current = p => {
      rotTarget.v = -Math.PI / 4 - p * Math.PI * 2;
    };
    const loop = () => {
      if (disposed) return;
      raf = requestAnimationFrame(loop);
      if (!visible) return;
      const t = (performance.now() - t0) / 1000;
      if (spin) {
        can.rotation.y = -Math.PI / 4 - t * 0.5;
        group.rotation.z = 0.05 * Math.sin(t * 0.7);
        group.rotation.x = 0.035 * Math.sin(t * 0.45 + 1.2);
        group.position.y = 0.04 * Math.sin(t * 0.9);
      } else {
        can.rotation.y += (rotTarget.v - can.rotation.y) * 0.16;
        group.rotation.z = 0.025 * Math.sin(t * 0.5);
      }
      renderer.render(scene, cam);
    };
    if (reduce) {
      renderer.render(scene, cam);
    } else loop();
    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      geo.dispose();
      tex.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
    };
  }, []);
  if (failed && fallback) return React.createElement(Turntable, {
    frames: fallback,
    auto: 3000,
    alt: alt
  });
  return React.createElement("div", {
    className: "can3d",
    ref: host,
    role: "img",
    "aria-label": alt
  });
}
Object.assign(window, {
  Can3D
});
;
if (window.gsap && window.ScrollTrigger) {
  gsap.registerPlugin(ScrollTrigger);
}
const DND_REDUCE = matchMedia('(prefers-reduced-motion: reduce)').matches;
const DND_GSAP = () => window.gsap && window.ScrollTrigger && !DND_REDUCE;
function CanScene() {
  const root = useRef(null);
  const canCtrl = useRef(null);
  useEffect(() => {
    if (!DND_GSAP()) return;
    const names = ['Sealed', 'Pure', 'Tested', 'Quiet'];
    const lbl = root.current.querySelector('.scene-eyebrow em');
    const mm = gsap.matchMedia();
    mm.add('(min-width: 561px)', () => {
      const ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: root.current,
            start: 'top top',
            end: '+=2800',
            scrub: 0.6,
            pin: true,
            anticipatePin: 1,
            onUpdate(self) {
              lbl.textContent = names[Math.min(3, self.progress * 4 | 0)];
              if (canCtrl.current) canCtrl.current(self.progress);
            }
          }
        });
        tl.fromTo('.scene-can', {
          y: 70,
          scale: 0.8,
          rotate: -4
        }, {
          y: -12,
          scale: 1.14,
          rotate: 3,
          duration: 10,
          ease: 'none'
        }, 0).fromTo('.scene-bg', {
          opacity: 0
        }, {
          opacity: 1,
          duration: 5,
          ease: 'sine.in'
        }, 0).to('.scene-bg', {
          opacity: 0,
          duration: 5,
          ease: 'sine.out'
        }, 5).fromTo('.scene-bar i', {
          scaleX: 0
        }, {
          scaleX: 1,
          duration: 10,
          ease: 'none'
        }, 0);
        gsap.utils.toArray('.scene-words span', root.current).forEach((w, i) => {
          const c = [1.7, 5, 8.3][i];
          tl.fromTo(w, {
            opacity: 0,
            yPercent: 12
          }, {
            opacity: 1,
            yPercent: 0,
            duration: 1.5,
            ease: 'power2.out'
          }, c - 1.5).to(w, {
            opacity: 0,
            yPercent: -9,
            duration: 1.5,
            ease: 'power2.in'
          }, c + 0.9);
        });
        gsap.utils.toArray('.scap', root.current).forEach((cEl, i) => {
          tl.fromTo(cEl, {
            autoAlpha: 0,
            y: 18
          }, {
            autoAlpha: 1,
            y: 0,
            duration: 1,
            ease: 'power2.out'
          }, 1 + i * 2.1).fromTo(cEl, {
            '--lead': 0
          }, {
            '--lead': 1,
            duration: 1.1,
            ease: 'none'
          }, 1 + i * 2.1);
        });
      }, root);
      return () => ctx.revert();
    });
    return () => mm.revert();
  }, []);
  const caps = [['01', 'Aluminium shell', 'Light- and air-tight. Nothing gets in, nothing leaches out.'], ['02', 'Zero plastic', 'No bottle, no liner, no microplastics. Ever.'], ['03', 'Purity, proven', 'Lab-tested for microplastics, PFAS and contaminants — every batch, published.'], ['04', 'Comes back', 'Infinitely recyclable. A new can in ~60 days.']];
  return React.createElement("section", {
    className: "scene",
    ref: root,
    "data-screen-label": "Anatomy"
  }, React.createElement("div", {
    className: "scene-sticky"
  }, React.createElement("div", {
    className: "scene-bg"
  }), React.createElement("div", {
    className: "scene-words",
    "aria-hidden": "true"
  }, React.createElement("span", null, "PURE"), React.createElement("span", null, "SIMPLE"), React.createElement("span", null, "STILL")), React.createElement("p", {
    className: "scene-eyebrow"
  }, "The anatomy of ", React.createElement("em", null, "quiet")), React.createElement("div", {
    className: "scene-can"
  }, React.createElement(Can3D, {
    texture: window.RES('still360n', 'assets/rot/still-360n.jpg'),
    fallback: window.DND.products[0].turn,
    ctrl: canCtrl,
    spin: false,
    alt: "DND can anatomy"
  })), caps.map(([n, t, d], i) => React.createElement("figure", {
    className: 'scap scap-' + i,
    key: n
  }, React.createElement("span", {
    className: "scap-n"
  }, n), React.createElement("div", null, React.createElement("h4", null, t), React.createElement("p", null, d)))), React.createElement("div", {
    className: "scene-bar"
  }, React.createElement("i", null))));
}
function MotionFX() {
  useEffect(() => {
    if (!window.gsap || DND_REDUCE || !matchMedia('(pointer:fine)').matches) return;
    let curBtn = null,
      curCard = null;
    const move = e => {
      const t = e.target instanceof Element ? e.target : null;
      const b = t && t.closest('.btn');
      if (curBtn && b !== curBtn) {
        gsap.to(curBtn, {
          x: 0,
          y: 0,
          duration: 0.7,
          ease: 'elastic.out(1,0.45)'
        });
        curBtn = null;
      }
      if (b) {
        curBtn = b;
        const r = b.getBoundingClientRect();
        gsap.to(b, {
          x: (e.clientX - r.left - r.width / 2) * 0.22,
          y: (e.clientY - r.top - r.height / 2) * 0.34,
          duration: 0.35,
          ease: 'power3.out'
        });
      }
      const c = t && t.closest('.pcard');
      if (curCard && c !== curCard) {
        gsap.to(curCard, {
          rotateX: 0,
          rotateY: 0,
          y: 0,
          duration: 0.7,
          ease: 'power3.out'
        });
        curCard = null;
      }
      if (c) {
        curCard = c;
        const r = c.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5,
          py = (e.clientY - r.top) / r.height - 0.5;
        gsap.to(c, {
          rotateY: px * 7,
          rotateX: -py * 7,
          y: -6,
          transformPerspective: 900,
          duration: 0.45,
          ease: 'power2.out'
        });
      }
    };
    document.addEventListener('mousemove', move, {
      passive: true
    });
    return () => document.removeEventListener('mousemove', move);
  }, []);
  return null;
}
Object.assign(window, {
  CanScene,
  MotionFX,
  DND_GSAP,
  DND_REDUCE
});
;
function Cheers() {
  return null;
}
Object.assign(window, {
  Cheers
});
;
const CartCtx = createContext(null);
const useCart = () => useContext(CartCtx);
function CartProvider({
  children
}) {
  const [items, setItems] = useState({});
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState('once');
  const add = (id, size = 12, q = 1) => {
    const key = id + ':' + size;
    setItems(s => ({
      ...s,
      [key]: (s[key] || 0) + q
    }));
    setOpen(true);
  };
  const setQty = (key, q) => setItems(s => {
    const n = {
      ...s
    };
    if (q <= 0) delete n[key];else n[key] = q;
    return n;
  });
  const count = Object.values(items).reduce((a, b) => a + b, 0);
  const byId = id => window.DND.products.find(p => p.id === id);
  const packPrice = (id, size) => {
    const p = byId(id);
    const pk = (p.packs || []).find(x => x.size === +size);
    return pk ? pk.price : p.price;
  };
  const subtotal = Object.entries(items).reduce((a, [key, q]) => {
    const [pid, size] = key.split(':');
    return a + packPrice(pid, size) * q;
  }, 0);
  const discount = plan === 'sub' ? Math.round(subtotal * 0.15) : 0;
  const total = subtotal - discount;
  return React.createElement(CartCtx.Provider, {
    value: {
      items,
      add,
      setQty,
      count,
      subtotal,
      discount,
      total,
      open,
      setOpen,
      plan,
      setPlan,
      byId,
      packPrice
    }
  }, children, React.createElement(CartDrawer, null));
}
function CartDrawer() {
  const [note, setNote] = useState(false);
  const c = useCart();
  const lines = Object.entries(c.items);
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: 'scrim' + (c.open ? ' show' : ''),
    onClick: () => c.setOpen(false)
  }), React.createElement("aside", {
    className: 'drawer' + (c.open ? ' show' : ''),
    "aria-hidden": !c.open
  }, React.createElement("header", {
    className: "drawer-head"
  }, React.createElement("span", {
    className: "kicker"
  }, "Your cart", c.count ? ' (' + c.count + ')' : ''), React.createElement("button", {
    className: "icon-btn",
    onClick: () => c.setOpen(false),
    "aria-label": "Close cart"
  }, React.createElement(Icon.close, {
    width: "20",
    height: "20"
  }))), lines.length === 0 ? React.createElement("div", {
    className: "drawer-empty"
  }, React.createElement(Icon.bag, {
    width: "40",
    height: "40"
  }), React.createElement("p", {
    className: "serif-it"
  }, "Quiet in here."), React.createElement("span", {
    className: "muted-s"
  }, "Your cart is empty."), React.createElement("button", {
    className: "btn btn-solid",
    onClick: () => c.setOpen(false)
  }, "Browse the water")) : React.createElement(React.Fragment, null, React.createElement("div", {
    className: "plan-toggle"
  }, React.createElement("button", {
    className: c.plan === 'once' ? 'on' : '',
    onClick: () => c.setPlan('once')
  }, "One-time"), React.createElement("button", {
    className: c.plan === 'sub' ? 'on' : '',
    onClick: () => c.setPlan('sub')
  }, "Subscribe ", React.createElement("em", null, "save 15%"))), React.createElement("div", {
    className: "drawer-lines"
  }, lines.map(([key, q]) => {
    const [pid, size] = key.split(':');
    const p = c.byId(pid);
    const price = c.packPrice(pid, size);
    return React.createElement("div", {
      className: "line",
      key: key
    }, React.createElement("div", {
      className: "line-img",
      style: {
        '--a': p.accent
      }
    }, React.createElement("img", {
      src: p.img,
      alt: p.full
    })), React.createElement("div", {
      className: "line-info"
    }, React.createElement("div", {
      className: "line-top"
    }, React.createElement("strong", null, React.createElement(DndMark, null), " ", p.name), React.createElement("span", null, '\u20b9' + (price * q).toLocaleString('en-IN'))), React.createElement("span", {
      className: "muted-s"
    }, size + ' \u00d7 330ml \u00b7 ' + p.full), React.createElement("div", {
      className: "qty"
    }, React.createElement("button", {
      onClick: () => c.setQty(key, q - 1),
      "aria-label": "Decrease"
    }, React.createElement(Icon.minus, {
      width: "15",
      height: "15"
    })), React.createElement("span", null, q), React.createElement("button", {
      onClick: () => c.setQty(key, q + 1),
      "aria-label": "Increase"
    }, React.createElement(Icon.plus, {
      width: "15",
      height: "15"
    })))));
  })), React.createElement("footer", {
    className: "drawer-foot"
  }, React.createElement("div", {
    className: "sum-row"
  }, React.createElement("span", null, "Subtotal"), React.createElement("span", null, '\u20b9' + c.subtotal.toLocaleString('en-IN'))), c.discount > 0 && React.createElement("div", {
    className: "sum-row disc"
  }, React.createElement("span", null, "Subscription saving"), React.createElement("span", null, '\u2212\u20b9' + c.discount.toLocaleString('en-IN'))), React.createElement("div", {
    className: "sum-row total"
  }, React.createElement("span", null, "Total"), React.createElement("span", null, '\u20b9' + c.total.toLocaleString('en-IN'))), React.createElement("button", {
    className: "btn btn-accent btn-block",
    style: {
      marginTop: 14
    },
    onClick: () => window.dndCheckout(c.items, c.plan, setNote)
  }, "Checkout"), note && React.createElement("p", {
    className: "ship-note",
    style: {
      color: 'var(--accent)'
    }
  }, "Preview build \u2014 checkout opens at launch."), React.createElement("p", {
    className: "ship-note"
  }, "Free carbon-neutral shipping \xB7 Pause or cancel anytime")))));
}
Object.assign(window, {
  CartProvider,
  useCart
});
;
function Announce() {
  return React.createElement("div", {
    className: "announce"
  }, React.createElement("div", {
    className: "wrap announce-row"
  }, React.createElement("span", null, "Free carbon-neutral shipping over ", '\u20b9', "999"), React.createElement("span", {
    className: "dot"
  }, "\xB7"), React.createElement("span", {
    className: "serif-it"
  }, "Drink No Drama.", '\u2122'), React.createElement("span", {
    className: "dot"
  }, "\xB7"), React.createElement("span", null, "Subscribe & save 15%")));
}
function Nav() {
  const c = useCart();
  const [solid, setSolid] = useState(false);
  const [menu, setMenu] = useState(false);
  useEffect(() => {
    const on = () => setSolid(window.scrollY > 10);
    on();
    window.addEventListener('scroll', on, {
      passive: true
    });
    return () => window.removeEventListener('scroll', on);
  }, []);
  useEffect(() => {
    document.body.style.overflow = menu ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menu]);
  const link = (label, href) => React.createElement("a", {
    href: href,
    className: "nav-link"
  }, label);
  const mlinks = [['Shop', '#shop'], ['Our Water', '#water'], ['Sustainability', '#planet'], ['About', '#story']];
  return React.createElement(React.Fragment, null, React.createElement("nav", {
    className: 'nav' + (solid ? ' solid' : '')
  }, React.createElement("div", {
    className: "wrap nav-row"
  }, React.createElement("div", {
    className: "nav-left"
  }, React.createElement("button", {
    className: "burger",
    onClick: () => setMenu(true),
    "aria-label": "Open menu"
  }, React.createElement("i", null), React.createElement("i", null)), link('Shop', '#shop'), link('Our Water', '#water'), link('Sustainability', '#planet')), React.createElement("a", {
    href: "#top",
    className: "nav-logo",
    "aria-label": "DND home"
  }, React.createElement(Wordmark, {
    size: 28,
    drink: true
  })), React.createElement("div", {
    className: "nav-right"
  }, link('About', '#story'), React.createElement("button", {
    className: "cart-btn",
    onClick: () => c.setOpen(true),
    "aria-label": "Open cart"
  }, React.createElement(Icon.bag, {
    width: "22",
    height: "22"
  }), c.count > 0 && React.createElement("span", {
    className: "cart-dot"
  }, c.count))))), React.createElement("div", {
    className: 'mnav' + (menu ? ' show' : ''),
    "aria-hidden": !menu
  }, React.createElement("div", {
    className: "mnav-head"
  }, React.createElement(Wordmark, {
    size: 30
  }), React.createElement("button", {
    className: "icon-btn",
    onClick: () => setMenu(false),
    "aria-label": "Close menu"
  }, React.createElement(Icon.close, {
    width: "22",
    height: "22"
  }))), React.createElement("div", {
    className: "mnav-links"
  }, mlinks.map(([l, h], i) => React.createElement("a", {
    href: h,
    key: l,
    onClick: () => setMenu(false),
    style: {
      transitionDelay: menu ? 0.1 + i * 0.06 + 's' : '0s'
    }
  }, l))), React.createElement("p", {
    className: "mnav-foot serif-it"
  }, "Pure. Simple. Still.")));
}
function Hero() {
  const ref = useRef(null);
  useEffect(() => {
    if (!DND_GSAP()) return;
    const ctx = gsap.context(() => {
      gsap.from('.sl-l', {
        yPercent: 118,
        rotate: 5,
        duration: 1.05,
        ease: 'power4.out',
        stagger: 0.045,
        delay: 0.15
      });
      gsap.from('.hero-can', {
        y: 70,
        autoAlpha: 0,
        duration: 1.3,
        ease: 'power3.out',
        delay: 0.45
      });
      const st = {
        trigger: ref.current,
        start: 'top top',
        end: 'bottom top',
        scrub: 0.5
      };
      gsap.to('.hero-can-wrap', {
        y: -90,
        ease: 'none',
        scrollTrigger: st
      });
      gsap.to('.hero-glow', {
        y: 130,
        ease: 'none',
        scrollTrigger: st
      });
      gsap.to('.hero-copy', {
        y: 50,
        autoAlpha: 0.25,
        ease: 'none',
        scrollTrigger: {
          ...st,
          start: '40% top'
        }
      });
    }, ref);
    return () => ctx.revert();
  }, []);
  const line = txt => React.createElement("span", {
    className: "sl-line",
    key: txt
  }, React.createElement("span", {
    className: "sl-in"
  }, [...txt].map((ch, i) => React.createElement("span", {
    className: "sl-l",
    key: i,
    style: ch === '.' ? {
      color: 'var(--accent)'
    } : ch === '\u2122' ? {
      fontSize: '0.26em',
      verticalAlign: '2.05em',
      fontWeight: 600,
      letterSpacing: '0'
    } : null
  }, ch))));
  return React.createElement("header", {
    className: "hero",
    id: "top",
    ref: ref,
    "data-screen-label": "Hero"
  }, React.createElement("div", {
    className: "hero-atmos"
  }, React.createElement("div", {
    className: "hero-glow"
  }), React.createElement("div", {
    className: "hero-mist"
  }), React.createElement("div", {
    className: "hero-grain"
  })), React.createElement("div", {
    className: "hero-inner wrap"
  }, React.createElement("div", {
    className: "hero-copy"
  }, React.createElement(Reveal, {
    as: "p",
    cls: "eyebrow rv-1"
  }, "Packaged drinking water \xB7 330ml"), React.createElement("h1", {
    className: "display hero-h",
    "aria-label": "Do Not Disturb."
  }, line('Do'), line('Not'), line('Disturb.' + '\u2122')), React.createElement(Reveal, {
    cls: "rv-3 hero-sub"
  }, React.createElement("p", {
    className: "serif-it"
  }, "Quiet hydration in a can."), React.createElement("p", {
    className: "hero-lede"
  }, "Purified still water, pH-balanced and triple-filtered. No plastic. No noise. No drama.")), React.createElement(Reveal, {
    cls: "rv-4 hero-cta"
  }, React.createElement("a", {
    href: "#lineup",
    className: "btn btn-solid"
  }, 'Shop the water'), React.createElement("a", {
    href: "#water",
    className: "btn btn-ghost"
  }, 'What\u2019s inside'))), React.createElement("div", {
    className: "hero-can-wrap"
  }, React.createElement("div", {
    className: "hero-can"
  }, React.createElement(Can3D, {
    texture: window.RES('still360n', 'assets/rot/still-360n.jpg'),
    fallback: window.DND.products[0].turn,
    alt: "DND Do Not Disturb still water can \u2014 rotating"
  }), React.createElement("div", {
    className: "hero-can-shadow"
  })), React.createElement("ul", {
    className: "hero-spec"
  }, React.createElement("li", null, React.createElement("span", null, "0"), "kcal"), React.createElement("li", null, React.createElement("span", null, "330"), "ml"), React.createElement("li", null, React.createElement("span", null, "100%"), "recyclable")))), React.createElement("div", {
    className: "scroll-cue"
  }, React.createElement("span", null, "Scroll"), React.createElement("i", null)));
}
function Ticker() {
  return React.createElement("div", {
    className: "ticker"
  }, React.createElement(Marquee, {
    items: ['Do Not Disturb\u2122', 'Pure', 'Simple', 'Still', 'Drink No Drama\u2122', 'Microplastic Tested', 'Quiet Hydration', 'BPA-Free'],
    speed: 48
  }));
}
function Manifesto() {
  return React.createElement("section", {
    className: "sec-pad manifesto",
    id: "story"
  }, React.createElement("div", {
    className: "wrap"
  }, React.createElement(Reveal, {
    as: "p",
    cls: "eyebrow center rv-1"
  }, "The whole idea"), React.createElement(Reveal, {
    cls: "rv-2"
  }, React.createElement("h2", {
    className: "display manifesto-h"
  }, "Water should be the ", React.createElement("span", {
    className: "hl"
  }, "quietest"), " thing you drink.")), React.createElement("div", {
    className: "manifesto-grid"
  }, React.createElement(Reveal, {
    cls: "rv-1"
  }, React.createElement("p", null, "We started DND because hydration got loud. Sugar, slogans, plastic, bright colours screaming for attention on a shelf. Water doesn", '\u2019', "t need any of that.")), React.createElement(Reveal, {
    cls: "rv-2"
  }, React.createElement("p", null, "So we made the opposite. Pure still water, balanced minerals, sealed in infinitely recyclable aluminium. A can that asks for nothing and gives you exactly what you came for.")), React.createElement(Reveal, {
    cls: "rv-3"
  }, React.createElement("p", {
    className: "serif-it big"
  }, "Pure. Simple. Still.", React.createElement("br", null), "Everything else is noise.")))));
}
function ProductCard({
  p,
  i
}) {
  const c = useCart();
  const [fi, setFi] = useState(0);
  const frames = p.turn || [p.canSide];
  return React.createElement(Reveal, {
    cls: 'pcard rv-' + (i + 1),
    style: {
      '--a': p.accent
    }
  }, React.createElement("div", {
    className: "pcard-media",
    onMouseEnter: () => setFi(Math.min(1, frames.length - 1)),
    onMouseLeave: () => setFi(0)
  }, p.badge && React.createElement("span", {
    className: "pcard-badge"
  }, p.badge), React.createElement("img", {
    src: frames[fi],
    alt: 'DND ' + p.full
  })), React.createElement("div", {
    className: "pcard-body"
  }, React.createElement("div", {
    className: "pcard-name"
  }, React.createElement("h3", {
    className: "display"
  }, React.createElement(DndMark, null), " ", p.name), React.createElement("span", {
    className: "pcard-price"
  }, '\u20b9' + p.price.toLocaleString('en-IN'), React.createElement("small", null, '\u20b9' + p.perCan + ' / can'))), React.createElement("p", {
    className: "serif-it pcard-tag"
  }, p.tag), React.createElement("p", {
    className: "pcard-blurb"
  }, p.blurb), React.createElement("div", {
    className: "pcard-mins"
  }, Object.entries(p.minerals).slice(0, 3).map(([k, v]) => React.createElement("span", {
    key: k
  }, React.createElement("b", null, v), k))), React.createElement("div", {
    className: "pcard-foot"
  }, React.createElement("button", {
    className: "btn btn-accent btn-block",
    onClick: () => c.add(p.id)
  }, 'Add ' + p.per + ' \u00b7 \u20b9' + p.price.toLocaleString('en-IN')))));
}
function Products() {
  return React.createElement("section", {
    className: "sec-pad shop",
    id: "shop"
  }, React.createElement("div", {
    className: "wrap"
  }, React.createElement("div", {
    className: "shop-head"
  }, React.createElement(Reveal, {
    as: "p",
    cls: "eyebrow rv-1"
  }, "Stock up"), React.createElement(Reveal, {
    as: "h2",
    cls: "display sec-h rv-2"
  }, "Three waters.", React.createElement("br", null), "One quiet promise."), React.createElement(Reveal, {
    as: "p",
    cls: "sec-lede rv-3"
  }, "Same clean source, same recyclable can. Pick how much you want it to do for you.")), React.createElement("div", {
    className: "pgrid"
  }, window.DND.products.map((p, i) => React.createElement(ProductCard, {
    p: p,
    i: i,
    key: p.id
  })))));
}
Object.assign(window, {
  Announce,
  Nav,
  Hero,
  Ticker,
  Manifesto,
  Products
});
;
function Water() {
  const ps = window.DND.products;
  const [sku, setSku] = useState('still');
  const p = ps.find(x => x.id === sku);
  const nut = window.DND.nutritionBySku[sku];
  return React.createElement("section", {
    className: "sec-pad water",
    id: "water"
  }, React.createElement("div", {
    className: "wrap water-grid"
  }, React.createElement("div", {
    className: "water-left"
  }, React.createElement(Reveal, {
    as: "p",
    cls: "eyebrow rv-1"
  }, "What", '\u2019', "s inside"), React.createElement(Reveal, {
    as: "h2",
    cls: "display sec-h rv-2"
  }, "Nothing you", '\u2019', "d", React.createElement("br", null), "taste. Everything", React.createElement("br", null), "you", '\u2019', "d want."), React.createElement(Reveal, {
    cls: "rv-3"
  }, React.createElement("p", {
    className: "sec-lede"
  }, "Zero sugar and zero calories across the line \u2014 and every batch lab-tested for microplastics, PFAS and contaminants. Pick a water; read it like the can.")), React.createElement(Reveal, {
    cls: "rv-4 water-stats"
  }, React.createElement("div", null, React.createElement("span", {
    className: "big-num"
  }, React.createElement(CountUp, {
    value: 0
  })), React.createElement("label", null, "kcal energy")), React.createElement("div", null, React.createElement("span", {
    className: "big-num"
  }, "7.4"), React.createElement("label", null, "pH balanced")), React.createElement("div", null, React.createElement("span", {
    className: "big-num"
  }, React.createElement(CountUp, {
    value: 100,
    suffix: "%"
  })), React.createElement("label", null, "lab tested")))), React.createElement(Reveal, {
    cls: "rv-2 nut-wrap"
  }, React.createElement("div", {
    className: "nut-tabs",
    role: "tablist",
    "aria-label": "Choose a water"
  }, ps.map(x => React.createElement("button", {
    key: x.id,
    role: "tab",
    "aria-selected": sku === x.id,
    className: 'nut-tab' + (sku === x.id ? ' on' : ''),
    style: {
      '--a': x.accent
    },
    onClick: () => setSku(x.id)
  }, x.name))), React.createElement("div", {
    className: "nut-flip",
    key: sku
  }, React.createElement("div", {
    className: "water-card metal",
    style: {
      '--a': p.accent
    }
  }, React.createElement("div", {
    className: "nut-head"
  }, React.createElement("span", {
    className: "kicker nut-sku"
  }, React.createElement(DndMark, null), " ", p.name), React.createElement("span", {
    className: "muted-s"
  }, "per 100ml")), React.createElement("ul", {
    className: "nut-list"
  }, nut.map(([k, v]) => React.createElement("li", {
    key: k
  }, React.createElement("span", null, k), React.createElement("i", null), React.createElement("span", {
    className: "nut-v"
  }, v)))), React.createElement("div", {
    className: "nut-foot"
  }, React.createElement("span", null, "Net quantity"), React.createElement("strong", null, "330 ml ", '\u212e')))))));
}
function Assurances() {
  const ic = [Icon.drop, Icon.hex, Icon.flask, Icon.recycle];
  return React.createElement("section", {
    className: "sec-pad assure"
  }, React.createElement("div", {
    className: "wrap"
  }, React.createElement(Reveal, {
    as: "h2",
    cls: "display sec-h center rv-1"
  }, "Tested quietly.", React.createElement("br", null), "Proven loudly."), React.createElement("div", {
    className: "assure-grid"
  }, window.DND.assurances.map(([t, d], i) => {
    const I = ic[i];
    return React.createElement(Reveal, {
      cls: 'assure-card rv-' + (i + 1),
      key: t
    }, React.createElement(I, {
      width: "30",
      height: "30"
    }), React.createElement("h3", null, t), React.createElement("p", null, d));
  }))));
}
function Planet() {
  return React.createElement("section", {
    className: "sec-pad planet",
    id: "planet"
  }, React.createElement("div", {
    className: "wrap planet-grid"
  }, React.createElement(Reveal, {
    cls: "rv-1 planet-figure"
  }, React.createElement("div", {
    className: "planet-badge metal"
  }, React.createElement("span", {
    className: "display"
  }, '\u221e'), React.createElement("small", null, "Infinitely recyclable")), React.createElement("div", {
    className: "planet-marks"
  }, React.createElement("span", {
    className: "pmark"
  }, React.createElement(Icon.recycle, {
    width: "17",
    height: "17"
  }), "Please recycle"), React.createElement("span", {
    className: "pmark"
  }, React.createElement(Icon.hex, {
    width: "17",
    height: "17"
  }), "BPA free")), React.createElement("img", {
    src: window.RES('canLid', 'assets/can-lid.png?v=2'),
    className: "planet-lid",
    alt: "DND can lid \u2014 Do Not Disturb \xB7 Pure \xB7 Simple \xB7 Still"
  })), React.createElement("div", {
    className: "planet-copy"
  }, React.createElement(Reveal, {
    as: "p",
    cls: "eyebrow rv-1"
  }, "Built to come back"), React.createElement(Reveal, {
    as: "h2",
    cls: "display sec-h rv-2"
  }, "Aluminium is", React.createElement("br", null), "forever. Plastic", React.createElement("br", null), "is a problem."), React.createElement(Reveal, {
    cls: "rv-3"
  }, React.createElement("p", {
    className: "sec-lede"
  }, "A can can be recycled endlessly without losing quality, and roughly 75% of all aluminium ever made is still in use today. Our cans ship plastic-free, BPA-free, and carbon-neutral.")), React.createElement(Reveal, {
    cls: "rv-4 planet-stats"
  }, React.createElement("div", null, React.createElement("span", {
    className: "big-num"
  }, React.createElement(CountUp, {
    value: 75,
    suffix: "%"
  })), React.createElement("label", null, "aluminium still in circulation")), React.createElement("div", null, React.createElement("span", {
    className: "big-num"
  }, React.createElement(CountUp, {
    value: 0
  })), React.createElement("label", null, "grams of plastic per can")), React.createElement("div", null, React.createElement("span", {
    className: "big-num"
  }, "60", React.createElement("small", null, "d")), React.createElement("label", null, "back on shelf as a new can"))))));
}
function Lifestyle() {
  return React.createElement("section", {
    className: "lifestyle"
  }, React.createElement("div", {
    className: "wrap life-grid"
  }, React.createElement(Reveal, {
    cls: "rv-1 life-tall"
  }, React.createElement("img", {
    className: "life-slot",
    src: window.RES('lifeStudio1', 'assets/life-studio-1c.jpg'),
    alt: "DND can on a desk in a calm workspace"
  })), React.createElement("div", {
    className: "life-col"
  }, React.createElement(Reveal, {
    cls: "rv-2 life-text"
  }, React.createElement("p", {
    className: "eyebrow"
  }, "Stillness, canned"), React.createElement("h2", {
    className: "display sec-h"
  }, "Make the", React.createElement("br", null), "room quieter."), React.createElement("p", {
    className: "sec-lede"
  }, "On the desk, by the bed, in the bag. DND is the calm object in a loud day \u2014 and it looks unreasonably good doing nothing."), React.createElement("a", {
    href: "#shop",
    className: "btn btn-solid"
  }, "Bring it home")))));
}
function Reviews() {
  const r = window.DND.reviews;
  return React.createElement("section", {
    className: "sec-pad reviews",
    id: "reviews"
  }, React.createElement("div", {
    className: "wrap"
  }, React.createElement("div", {
    className: "rev-head"
  }, React.createElement(Reveal, {
    cls: "rv-1 stars"
  }, [0, 1, 2, 3, 4].map(i => React.createElement(Icon.star, {
    key: i,
    width: "18",
    height: "18"
  })), React.createElement("span", null, "4.9 \xB7 2,100+ quiet sips")), React.createElement(Reveal, {
    as: "h2",
    cls: "display sec-h center rv-2"
  }, "People who", '\u2019', "d", React.createElement("br", null), "rather not shout.")), React.createElement("div", {
    className: "rev-grid"
  }, r.map(([q, n, p], i) => React.createElement(Reveal, {
    cls: 'rev-card rv-' + (i % 3 + 1),
    key: i
  }, React.createElement("div", {
    className: "rev-stars"
  }, [0, 1, 2, 3, 4].map(j => React.createElement(Icon.star, {
    key: j,
    width: "13",
    height: "13"
  }))), React.createElement("p", {
    className: "rev-q"
  }, '\u201c', q, '\u201d'), React.createElement("div", {
    className: "rev-by"
  }, React.createElement("strong", null, n), React.createElement("span", null, React.createElement(DndMark, null), " ", p)))))));
}
function Faq() {
  const [open, setOpen] = useState(0);
  return React.createElement("section", {
    className: "sec-pad faq",
    id: "faq"
  }, React.createElement("div", {
    className: "wrap faq-grid"
  }, React.createElement("div", {
    className: "faq-left"
  }, React.createElement(Reveal, {
    as: "p",
    cls: "eyebrow rv-1"
  }, "Before you ask"), React.createElement(Reveal, {
    as: "h2",
    cls: "display sec-h rv-2"
  }, "Quietly", React.createElement("br", null), "answered.")), React.createElement("div", {
    className: "faq-list"
  }, window.DND.faqs.map(([q, a], i) => React.createElement(Reveal, {
    cls: 'faq-item rv-' + (i % 3 + 1) + (open === i ? ' open' : ''),
    key: i
  }, React.createElement("button", {
    className: "faq-q",
    "aria-expanded": open === i,
    onClick: () => setOpen(open === i ? -1 : i)
  }, React.createElement("span", null, q), React.createElement(Icon.chev, {
    className: "faq-chev",
    width: "22",
    height: "22"
  })), React.createElement("div", {
    className: "faq-a"
  }, React.createElement("p", null, a)))))));
}
function Newsletter() {
  const [done, setDone] = useState(false);
  return React.createElement("section", {
    className: "news"
  }, React.createElement("div", {
    className: "wrap"
  }, React.createElement(Reveal, {
    cls: "rv-1 news-card metal"
  }, React.createElement("div", {
    className: "news-inner"
  }, React.createElement("p", {
    className: "eyebrow"
  }, "Keep it quiet with us"), React.createElement("h2", {
    className: "display sec-h"
  }, "Drink No Drama.", '\u2122', React.createElement("br", null), "Hear from us rarely."), React.createElement("p", {
    className: "sec-lede"
  }, "Restocks, new waters, and the occasional quiet note. No spam, ever."), done ? React.createElement("p", {
    className: "news-thanks serif-it"
  }, "You", '\u2019', "re in. We", '\u2019', "ll be quiet about it.") : React.createElement("form", {
    className: "news-form",
    onSubmit: e => {
      e.preventDefault();
      setDone(true);
    }
  }, React.createElement("input", {
    type: "email",
    required: true,
    placeholder: "you@quiet.com",
    "aria-label": "Email",
    autoComplete: "email",
    inputMode: "email"
  }), React.createElement("button", {
    className: "btn btn-accent",
    type: "submit"
  }, "Notify me"))))));
}
function Footer() {
  const col = (h, items) => React.createElement("div", {
    className: "foot-col"
  }, React.createElement("span", {
    className: "kicker"
  }, h), React.createElement("ul", null, items.map(([x, href]) => React.createElement("li", {
    key: x
  }, React.createElement("a", {
    href: href
  }, x)))));
  return React.createElement("footer", {
    className: "footer"
  }, React.createElement("div", {
    className: "wrap"
  }, React.createElement("div", {
    className: "foot-top"
  }, React.createElement("div", {
    className: "foot-brand"
  }, React.createElement(Wordmark, {
    size: 46,
    color: "var(--bg-2)",
    drink: true
  }), React.createElement("p", {
    className: "serif-it"
  }, "Quiet hydration.", React.createElement("br", null), "Drink No Drama.", '\u2122')), React.createElement("div", {
    className: "foot-cols"
  }, col('Shop', [['Still', '#shop'], ['Sparkling', '#shop'], ['Wellness', '#shop'], ['Subscriptions', '#shop'], ['Gift a case', '#shop']]), col('Brand', [['Our story', '#story'], ['Sustainability', '#planet'], ['What\u2019s inside', '#water'], ['Reviews', '#reviews']]), col('Help', [['FAQ', '#faq'], ['Shipping', '#faq'], ['Returns', '#faq'], ['Contact', 'mailto:hello@justdnd.com']]))), React.createElement("div", {
    className: "foot-bar"
  }, React.createElement("span", null, "\xA9 2026 ", React.createElement(DndMark, null), " \u2014 Do Not Disturb", '\u2122', ". Packaged Drinking Water."), React.createElement("span", {
    className: "foot-mini"
  }, "Pure \xB7 Simple \xB7 Still"))));
}
Object.assign(window, {
  Water,
  Assurances,
  Planet,
  Lifestyle,
  Reviews,
  Faq,
  Newsletter,
  Footer
});
;
function Lineup() {
  const root = useRef(null),
    track = useRef(null),
    pin = useRef(null),
    cnt = useRef(null);
  const ps = window.DND.products;
  const cart = useCart();
  const [packSel, setPackSel] = useState({});
  useEffect(() => {
    if (!window.gsap || !window.ScrollTrigger) return;
    const mm = gsap.matchMedia();
    mm.add('(min-width: 761px) and (prefers-reduced-motion: no-preference)', () => {
      const panels = gsap.utils.toArray('.lpanel', track.current);
      const dots = gsap.utils.toArray('.ldot', root.current);
      const n = panels.length;
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: 'top top',
          end: '+=3200',
          scrub: 0.7,
          pin: true,
          anticipatePin: 1,
          snap: {
            snapTo: 1 / (n - 1),
            duration: {
              min: 0.25,
              max: 0.6
            },
            ease: 'power1.inOut'
          },
          onUpdate(self) {
            const i = Math.round(self.progress * (n - 1));
            if (cnt.current) cnt.current.textContent = '0' + (i + 1) + ' / 0' + n;
            dots.forEach((d, j) => d.classList.toggle('on', j === i));
          }
        }
      });
      tl.to(track.current, {
        xPercent: -100 * (n - 1) / n,
        ease: 'none',
        duration: n - 1
      }, 0);
      ps.forEach((p, i) => {
        if (i === 0) return;
        tl.to(pin.current, {
          backgroundColor: p.theme.bg,
          color: p.theme.ink,
          duration: 0.45,
          ease: 'none'
        }, i - 0.72);
      });
      panels.forEach((panel, i) => {
        const can = panel.querySelector('.lpanel-can');
        const word = panel.querySelector('.lpanel-word');
        if (i === 0) {
          tl.fromTo(can, {
            x: 0
          }, {
            x: -40,
            ease: 'none',
            duration: 1
          }, 0);
          tl.fromTo(word, {
            xPercent: 0
          }, {
            xPercent: 6,
            ease: 'none',
            duration: 1
          }, 0);
        } else {
          tl.fromTo(can, {
            x: 60
          }, {
            x: i < n - 1 ? -40 : 0,
            ease: 'none',
            duration: i < n - 1 ? 2 : 1
          }, i - 1);
          tl.fromTo(word, {
            xPercent: -6
          }, {
            xPercent: i < n - 1 ? 6 : 0,
            ease: 'none',
            duration: i < n - 1 ? 2 : 1
          }, i - 1);
        }
      });
      return () => tl.scrollTrigger && tl.scrollTrigger.kill();
    });
    return () => mm.revert();
  }, []);
  return React.createElement("section", {
    className: "lineup",
    id: "lineup",
    ref: root,
    "data-screen-label": "Lineup"
  }, React.createElement("div", {
    className: "lineup-pin",
    ref: pin,
    style: {
      backgroundColor: ps[0].theme.bg,
      color: ps[0].theme.ink
    }
  }, React.createElement("header", {
    className: "lineup-head"
  }, React.createElement("span", {
    className: "eyebrow",
    style: {
      color: 'currentColor',
      opacity: .55
    }
  }, "The lineup"), React.createElement("span", {
    className: "lineup-count",
    ref: cnt
  }, "01 / 03")), React.createElement("div", {
    className: "ltrack",
    ref: track
  }, ps.map((p, i) => React.createElement("article", {
    className: "lpanel",
    key: p.id,
    style: {
      '--pa': p.accent,
      '--pbg': p.theme.bg,
      '--pink': p.theme.ink,
      '--psub': p.theme.sub
    }
  }, React.createElement("span", {
    className: "lpanel-word",
    "aria-hidden": "true"
  }, p.name), React.createElement("div", {
    className: "lpanel-can",
    onMouseEnter: e => {
      const img = e.currentTarget.querySelector('img');
      if (p.turn && p.turn[1]) img.src = p.turn[1];
      if (window.gsap) gsap.fromTo(e.currentTarget, {
        rotate: 0
      }, {
        rotate: 2.5,
        duration: 0.3,
        yoyo: true,
        repeat: 1,
        ease: 'power1.inOut'
      });
    },
    onMouseLeave: e => {
      e.currentTarget.querySelector('img').src = p.img;
    }
  }, React.createElement("img", {
    src: p.img,
    alt: 'DND ' + p.full + ' can'
  }), p.id === 'wellness' && React.createElement("span", {
    "aria-label": "Coming soon",
    style: {
      position: 'absolute',
      top: '5%',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 4,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      fontFamily: 'var(--body)',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '.24em',
      textTransform: 'uppercase',
      color: '#fff',
      background: 'var(--pa)',
      padding: '8px 16px 7px',
      borderRadius: '999px',
      boxShadow: '0 10px 26px rgba(8,10,12,.30)'
    }
  }, "Coming Soon")), React.createElement("div", {
    className: "lpanel-info"
  }, React.createElement("p", {
    className: "lpanel-idx"
  }, "0", i + 1, " \u2014 ", p.full), React.createElement("h3", {
    className: "display lpanel-name"
  }, p.name), React.createElement("p", {
    className: "lpanel-tag serif-it"
  }, p.tag), React.createElement("p", {
    className: "lpanel-blurb"
  }, p.blurb), React.createElement("div", {
    className: "pack-row",
    role: "radiogroup",
    "aria-label": "Pack size"
  }, (p.packs || []).map(pk => {
    const sel = (packSel[p.id] || 12) === pk.size;
    return React.createElement("button", {
      key: pk.size,
      role: "radio",
      "aria-checked": sel,
      className: 'pack-btn' + (sel ? ' on' : ''),
      onClick: () => setPackSel(s => ({
        ...s,
        [p.id]: pk.size
      }))
    }, React.createElement("b", null, pk.size), React.createElement("span", null, '\u20b9' + pk.price.toLocaleString('en-IN')), React.createElement("em", null, '\u20b9' + Math.round(pk.price / pk.size) + '/can'));
  })), React.createElement("button", {
    className: "btn lpanel-btn",
    onClick: () => cart.add(p.id, packSel[p.id] || 12)
  }, 'Add ' + (packSel[p.id] || 12) + '-pack \u00b7 \u20b9' + ((p.packs || []).find(x => x.size === (packSel[p.id] || 12)) || {
    price: p.price
  }).price.toLocaleString('en-IN')))))), React.createElement("div", {
    className: "lineup-dots",
    "aria-hidden": "true"
  }, ps.map((p, i) => React.createElement("i", {
    className: 'ldot' + (i === 0 ? ' on' : ''),
    key: p.id
  })))));
}
Object.assign(window, {
  Lineup
});
;
const __TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    transform:scale(var(--dc-inv-zoom,1));transform-origin:bottom right;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.15) transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.25);
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:rgba(41,38,27,.5);font-variant-numeric:tabular-nums}

  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;box-sizing:border-box;width:100%;min-width:0;height:26px;padding:0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;
    background:rgba(255,255,255,.6);color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:rgba(0,0,0,.25);background:rgba(255,255,255,.85)}
  select.twk-field{padding-right:22px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(0,0,0,.5)' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 8px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:rgba(0,0,0,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:14px;height:14px;border-radius:50%;background:#fff;
    border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;
    background:#fff;border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:rgba(0,0,0,.06);user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:rgba(255,255,255,.9);box-shadow:0 1px 2px rgba(0,0,0,.12);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:inherit;font:inherit;font-weight:500;min-height:22px;
    border-radius:6px;cursor:default;padding:4px 6px;line-height:1.2;
    overflow-wrap:anywhere}

  .twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
    background:rgba(0,0,0,.15);transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#34c759}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(14px)}

  .twk-num{display:flex;align-items:center;box-sizing:border-box;min-width:0;height:26px;padding:0 0 0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;background:rgba(255,255,255,.6)}
  .twk-num-lbl{font-weight:500;color:rgba(41,38,27,.6);cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 8px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:8px;color:rgba(41,38,27,.45)}

  .twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
    background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:rgba(0,0,0,.88)}
  .twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
  .twk-btn.secondary:hover{background:rgba(0,0,0,.1)}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:22px;
    border:.5px solid rgba(0,0,0,.1);border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}

  .twk-chips{display:flex;gap:6px}
  .twk-chip{position:relative;appearance:none;flex:1;min-width:0;height:46px;
    padding:0;border:0;border-radius:6px;overflow:hidden;cursor:default;
    box-shadow:0 0 0 .5px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.06);
    transition:transform .12s cubic-bezier(.3,.7,.4,1),box-shadow .12s}
  .twk-chip:hover{transform:translateY(-1px);
    box-shadow:0 0 0 .5px rgba(0,0,0,.18),0 4px 10px rgba(0,0,0,.12)}
  .twk-chip[data-on="1"]{box-shadow:0 0 0 1.5px rgba(0,0,0,.85),
    0 2px 6px rgba(0,0,0,.15)}
  .twk-chip>span{position:absolute;top:0;bottom:0;right:0;width:34%;
    display:flex;flex-direction:column;box-shadow:-1px 0 0 rgba(0,0,0,.1)}
  .twk-chip>span>i{flex:1;box-shadow:0 -1px 0 rgba(0,0,0,.1)}
  .twk-chip>span>i:first-child{box-shadow:none}
  .twk-chip svg{position:absolute;top:6px;left:6px;width:13px;height:13px;
    filter:drop-shadow(0 1px 1px rgba(0,0,0,.3))}
`;
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  const setTweak = React.useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null ? keyOrEdits : {
      [keyOrEdits]: val
    };
    setValues(prev => ({
      ...prev,
      ...edits
    }));
    window.parent.postMessage({
      type: '__edit_mode_set_keys',
      edits
    }, '*');
    window.dispatchEvent(new CustomEvent('tweakchange', {
      detail: edits
    }));
  }, []);
  return [values, setTweak];
}
function TweaksPanel({
  title = 'Tweaks',
  children
}) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({
    x: 16,
    y: 16
  });
  const PAD = 16;
  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth,
      h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y))
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);
  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);
  React.useEffect(() => {
    const onMsg = e => {
      const t = e?.data?.type;
      if (t === '__activate_edit_mode') setOpen(true);else if (t === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({
      type: '__edit_mode_available'
    }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);
  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({
      type: '__edit_mode_dismissed'
    }, '*');
  };
  const onDragStart = e => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX,
      sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = ev => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy)
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };
  if (!open) return null;
  return React.createElement(React.Fragment, null, React.createElement("style", null, __TWEAKS_STYLE), React.createElement("div", {
    ref: dragRef,
    className: "twk-panel",
    "data-omelette-chrome": "",
    style: {
      right: offsetRef.current.x,
      bottom: offsetRef.current.y
    }
  }, React.createElement("div", {
    className: "twk-hd",
    onMouseDown: onDragStart
  }, React.createElement("b", null, title), React.createElement("button", {
    className: "twk-x",
    "aria-label": "Close tweaks",
    onMouseDown: e => e.stopPropagation(),
    onClick: dismiss
  }, "\u2715")), React.createElement("div", {
    className: "twk-body"
  }, children)));
}
function TweakSection({
  label,
  children
}) {
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: "twk-sect"
  }, label), children);
}
function TweakRow({
  label,
  value,
  children,
  inline = false
}) {
  return React.createElement("div", {
    className: inline ? 'twk-row twk-row-h' : 'twk-row'
  }, React.createElement("div", {
    className: "twk-lbl"
  }, React.createElement("span", null, label), value != null && React.createElement("span", {
    className: "twk-val"
  }, value)), children);
}
function TweakSlider({
  label,
  value,
  min = 0,
  max = 100,
  step = 1,
  unit = '',
  onChange
}) {
  return React.createElement(TweakRow, {
    label: label,
    value: `${value}${unit}`
  }, React.createElement("input", {
    type: "range",
    className: "twk-slider",
    min: min,
    max: max,
    step: step,
    value: value,
    onChange: e => onChange(Number(e.target.value))
  }));
}
function TweakToggle({
  label,
  value,
  onChange
}) {
  return React.createElement("div", {
    className: "twk-row twk-row-h"
  }, React.createElement("div", {
    className: "twk-lbl"
  }, React.createElement("span", null, label)), React.createElement("button", {
    type: "button",
    className: "twk-toggle",
    "data-on": value ? '1' : '0',
    role: "switch",
    "aria-checked": !!value,
    onClick: () => onChange(!value)
  }, React.createElement("i", null)));
}
function TweakRadio({
  label,
  value,
  options,
  onChange
}) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const labelLen = o => String(typeof o === 'object' ? o.label : o).length;
  const maxLen = options.reduce((m, o) => Math.max(m, labelLen(o)), 0);
  const fitsAsSegments = maxLen <= ({
    2: 16,
    3: 10
  }[options.length] ?? 0);
  if (!fitsAsSegments) {
    const resolve = s => {
      const m = options.find(o => String(typeof o === 'object' ? o.value : o) === s);
      return m === undefined ? s : typeof m === 'object' ? m.value : m;
    };
    return React.createElement(TweakSelect, {
      label: label,
      value: value,
      options: options,
      onChange: s => onChange(resolve(s))
    });
  }
  const opts = options.map(o => typeof o === 'object' ? o : {
    value: o,
    label: o
  });
  const idx = Math.max(0, opts.findIndex(o => o.value === value));
  const n = opts.length;
  const segAt = clientX => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor((clientX - r.left - 2) / inner * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };
  const onPointerDown = e => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = ev => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return React.createElement(TweakRow, {
    label: label
  }, React.createElement("div", {
    ref: trackRef,
    role: "radiogroup",
    onPointerDown: onPointerDown,
    className: dragging ? 'twk-seg dragging' : 'twk-seg'
  }, React.createElement("div", {
    className: "twk-seg-thumb",
    style: {
      left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
      width: `calc((100% - 4px) / ${n})`
    }
  }), opts.map(o => React.createElement("button", {
    key: o.value,
    type: "button",
    role: "radio",
    "aria-checked": o.value === value
  }, o.label))));
}
function TweakSelect({
  label,
  value,
  options,
  onChange
}) {
  return React.createElement(TweakRow, {
    label: label
  }, React.createElement("select", {
    className: "twk-field",
    value: value,
    onChange: e => onChange(e.target.value)
  }, options.map(o => {
    const v = typeof o === 'object' ? o.value : o;
    const l = typeof o === 'object' ? o.label : o;
    return React.createElement("option", {
      key: v,
      value: v
    }, l);
  })));
}
function TweakText({
  label,
  value,
  placeholder,
  onChange
}) {
  return React.createElement(TweakRow, {
    label: label
  }, React.createElement("input", {
    className: "twk-field",
    type: "text",
    value: value,
    placeholder: placeholder,
    onChange: e => onChange(e.target.value)
  }));
}
function TweakNumber({
  label,
  value,
  min,
  max,
  step = 1,
  unit = '',
  onChange
}) {
  const clamp = n => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({
    x: 0,
    val: 0
  });
  const onScrubStart = e => {
    e.preventDefault();
    startRef.current = {
      x: e.clientX,
      val: value
    };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = ev => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return React.createElement("div", {
    className: "twk-num"
  }, React.createElement("span", {
    className: "twk-num-lbl",
    onPointerDown: onScrubStart
  }, label), React.createElement("input", {
    type: "number",
    value: value,
    min: min,
    max: max,
    step: step,
    onChange: e => onChange(clamp(Number(e.target.value)))
  }), unit && React.createElement("span", {
    className: "twk-num-unit"
  }, unit));
}
function __twkIsLight(hex) {
  const h = String(hex).replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, c => c + c) : h.padEnd(6, '0');
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = n >> 16 & 255,
    g = n >> 8 & 255,
    b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}
const __TwkCheck = ({
  light
}) => React.createElement("svg", {
  viewBox: "0 0 14 14",
  "aria-hidden": "true"
}, React.createElement("path", {
  d: "M3 7.2 5.8 10 11 4.2",
  fill: "none",
  strokeWidth: "2.2",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  stroke: light ? 'rgba(0,0,0,.78)' : '#fff'
}));
function TweakColor({
  label,
  value,
  options,
  onChange
}) {
  if (!options || !options.length) {
    return React.createElement("div", {
      className: "twk-row twk-row-h"
    }, React.createElement("div", {
      className: "twk-lbl"
    }, React.createElement("span", null, label)), React.createElement("input", {
      type: "color",
      className: "twk-swatch",
      value: value,
      onChange: e => onChange(e.target.value)
    }));
  }
  const key = o => String(JSON.stringify(o)).toLowerCase();
  const cur = key(value);
  return React.createElement(TweakRow, {
    label: label
  }, React.createElement("div", {
    className: "twk-chips",
    role: "radiogroup"
  }, options.map((o, i) => {
    const colors = Array.isArray(o) ? o : [o];
    const [hero, ...rest] = colors;
    const sup = rest.slice(0, 4);
    const on = key(o) === cur;
    return React.createElement("button", {
      key: i,
      type: "button",
      className: "twk-chip",
      role: "radio",
      "aria-checked": on,
      "data-on": on ? '1' : '0',
      "aria-label": colors.join(', '),
      title: colors.join(' · '),
      style: {
        background: hero
      },
      onClick: () => onChange(o)
    }, sup.length > 0 && React.createElement("span", null, sup.map((c, j) => React.createElement("i", {
      key: j,
      style: {
        background: c
      }
    }))), on && React.createElement(__TwkCheck, {
      light: __twkIsLight(hero)
    }));
  })));
}
function TweakButton({
  label,
  onClick,
  secondary = false
}) {
  return React.createElement("button", {
    type: "button",
    className: secondary ? 'twk-btn secondary' : 'twk-btn',
    onClick: onClick
  }, label);
}
Object.assign(window, {
  useTweaks,
  TweaksPanel,
  TweakSection,
  TweakRow,
  TweakSlider,
  TweakToggle,
  TweakRadio,
  TweakSelect,
  TweakText,
  TweakNumber,
  TweakColor,
  TweakButton
});
;
const TWEAK_DEFAULTS = {
  "mood": "aluminum",
  "accent": "#2d6cb2",
  "display": "Archivo",
  "heroLayout": "split",
  "grain": true
};
const FONTS = {
  'Archivo': 'Archivo, system-ui, sans-serif',
  'Space Grotesk': "'Space Grotesk', system-ui, sans-serif",
  'Hanken Grotesk': "'Hanken Grotesk', system-ui, sans-serif"
};
function App() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  useEffect(() => {
    const r = document.documentElement;
    r.setAttribute('data-mood', t.mood);
    r.style.setProperty('--accent', t.accent);
    r.style.setProperty('--display', FONTS[t.display] || FONTS.Archivo);
    document.body.classList.toggle('no-grain', !t.grain);
    document.body.classList.toggle('hero-centered', t.heroLayout === 'centered');
  }, [t]);
  useEffect(() => {
    if (window.ScrollTrigger) {
      const id = setTimeout(() => ScrollTrigger.refresh(), 450);
      return () => clearTimeout(id);
    }
  }, [t.display, t.heroLayout, t.mood]);
  return React.createElement(CartProvider, null, React.createElement(MotionFX, null), React.createElement(Announce, null), React.createElement(Nav, null), React.createElement(Hero, null), React.createElement(Ticker, null), React.createElement(Lineup, null), React.createElement(Manifesto, null), React.createElement(CanScene, null), React.createElement(Products, null), React.createElement(Water, null), React.createElement(Assurances, null), React.createElement(Planet, null), React.createElement(Lifestyle, null), React.createElement(Reviews, null), React.createElement(Faq, null), React.createElement(Newsletter, null), React.createElement(Footer, null), React.createElement(TweaksPanel, null, React.createElement(TweakSection, {
    label: "Mood"
  }), React.createElement(TweakRadio, {
    label: "Theme",
    value: t.mood,
    options: ['aluminum', 'mist', 'graphite'],
    onChange: v => setTweak('mood', v)
  }), React.createElement(TweakColor, {
    label: "Accent",
    value: t.accent,
    options: ['#2d6cb2', '#b08a2e', '#159a8e', '#16181b', '#7a5ad6'],
    onChange: v => setTweak('accent', v)
  }), React.createElement(TweakSection, {
    label: "Type"
  }), React.createElement(TweakSelect, {
    label: "Display font",
    value: t.display,
    options: ['Archivo', 'Space Grotesk', 'Hanken Grotesk'],
    onChange: v => setTweak('display', v)
  }), React.createElement(TweakSection, {
    label: "Layout"
  }), React.createElement(TweakRadio, {
    label: "Hero",
    value: t.heroLayout,
    options: ['split', 'centered'],
    onChange: v => setTweak('heroLayout', v)
  }), React.createElement(TweakToggle, {
    label: "Grain texture",
    value: t.grain,
    onChange: v => setTweak('grain', v)
  })));
}
ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(App, null));